import { CdkMenuModule } from '@angular/cdk/menu';
import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
    IFilterRun,
    IGroupRun,
    IHideRun,
    IPinOrUnpinRun,
    IRenameData,
    ISortRun,
    ISummarizeRun,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.interface';
import { TGroupCustomAction } from '@app/modules/table/children/table-column-action/interfaces/table-column-action.type';
import { IDataColumnMenu } from '@app/modules/table/children/table-column-header-menu/table-column-header-menu.interface';
import { TableRenameColumnComponent } from '@app/modules/table/children/table-rename-column/table-rename-column.component';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import {
    FuiButtonComponent,
    FuiIconComponent,
    FuiMenuComponent,
    FuiMenuItemComponent,
    FuiTooltipDirective,
} from '@fui/fui-components';
import { IMainLang } from '@interfaces/languages/main-lang';
import { PaletteLegendComponent } from '@modules/palettes/children/palette-legend/palette-legend.component';
import { IPaletteRecord } from '@modules/palettes/interfaces/palettes.interface';
import { EscapeHtmlPipe } from '@pipes/escape-html/escape-html.pipe';
import { Subject } from 'rxjs';

/**
 * Компонент меню заголовка колонки
 */
@Component({
    selector: 'app-table-column-header-menu',
    templateUrl: './table-column-header-menu.component.html',
    styleUrls: ['../styles/table-column-header-menu.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkMenuModule,
        MatIconModule,
        FuiButtonComponent,
        FuiIconComponent,
        FuiMenuComponent,
        FuiMenuItemComponent,
        FuiTooltipDirective,
        EscapeHtmlPipe,
        NgIf,
        FuiIconComponent,
        TableRenameColumnComponent,
        AsyncPipe,
        PaletteLegendComponent,
        NgTemplateOutlet,
        NgClass,
    ],
})
export class TableColumnHeaderMenuComponent {
    readonly dictionary: IMainLang = inject(LANG_DICTIONARY_TOKEN);

    /** Данные для Popup изменения названия столбца */
    renameData$: Subject<IRenameData> = new Subject();

    @ViewChild('headerBlock', { read: ElementRef }) headerBlockRef!: ElementRef<HTMLElement>;

    /** Компонент изменения названия столбца (Popup) */
    @ViewChild('tableRenameColumnComponent', { static: false, read: TableRenameColumnComponent })
    tableRenameColumnComponent!: TableRenameColumnComponent;

    /** Признак работы с таблицей реестра */
    @Input() isTableRegistry: boolean = false;

    /** Данные колонки */
    @Input() columnData: IDataColumnMenu | null = null;

    /** Данные колонки палитры (могут быть не переданы) */
    @Input() columnPalette: IPaletteRecord;

    /** Данные для запуска действия */
    @Output() filterRun = new EventEmitter<IFilterRun>();
    @Output() sortRun = new EventEmitter<ISortRun>();
    @Output() groupRun = new EventEmitter<IGroupRun[]>();
    @Output() summarizeRun = new EventEmitter<ISummarizeRun>();
    @Output() hideRun = new EventEmitter<IHideRun>();
    @Output() pinToggleRun = new EventEmitter<IPinOrUnpinRun>();
    @Output() groupCustomAction = new EventEmitter<TGroupCustomAction>();

    filterRunEmitted() {
        const position = this.#getPopupPosition();
        this.filterRun.emit({
            userColumnName: this.columnData?.name ?? '',
            searchValue: null,
            left: position.left,
            bottom: position.bottom,
        });
    }

    /**
     * Показывать пункт меню "Сгруппировать" только в реестрах, и не показывать для типов данных "Файл" и подобных
     */
    showGroupMenuItem() {
        return (
            this.isTableRegistry &&
            this.columnData.factor_type !== 'file' &&
            this.columnData.factor_type !== 'gallery' &&
            this.columnData.factor_type !== 'file_versions'
        );
    }

    /**
     * Отобразить Popup изменения имени столбца
     * @param name имя столбца
     * @param title человекочитаемое имя столбца
     */
    showRenamePopup(name: string, title: string) {
        const position = this.#getPopupPosition();
        this.renameData$.next({
            titlePopup: this.dictionary.t_rename_column,
            titleColumn: title,
            nameColumn: name,
        });

        this.tableRenameColumnComponent.show(position.left, position.bottom);
    }

    /**
     * Получить позицию отображения popup
     */
    #getPopupPosition() {
        const rect = this.headerBlockRef.nativeElement.getBoundingClientRect();
        return { left: rect.left, bottom: rect.bottom };
    }
}
